import { forwardRef } from 'react';
import { useTranslation } from '@workspace/i18n';
import { LoadingIcon } from '@workspace/ui/components/loading-icon';
import { getBootstrapLoadingText } from '../../i18n/bootstrap-loading-texts';
import { cn } from '../../lib/utils';
import type { PageLoadingProps } from './types';

const PAGE_LOADING_TEXT_KEY = 'common.pageLoading.entering';

interface LoadingTextProps {
    text: string;
    iconTheme?: PageLoadingProps['iconTheme'];
    renderText?: PageLoadingProps['renderText'];
}

function LoadingText({ text, iconTheme, renderText }: LoadingTextProps) {
    return (
        <span
            className={cn(
                'text-sm font-inter',
                iconTheme === 'light' ? 'text-white/60' : 'text-muted-foreground'
            )}
            data-testid="page-loading-text"
        >
            {renderText ? renderText(text) : text}
        </span>
    );
}

function LocalizedLoadingText({ iconTheme, renderText }: Omit<LoadingTextProps, 'text'>) {
    const { t, i18n, ready } = useTranslation('common');
    const canShowDefaultText = ready && i18n.exists(PAGE_LOADING_TEXT_KEY);

    // i18n 快照未就绪时用同源镜像文案兜底，保证门与文案同时出现；
    // 就绪后回 t()（与镜像同源于 locales JSON，文案不变）。
    const text = canShowDefaultText
        ? t('pageLoading.entering')
        : getBootstrapLoadingText(i18n.language);

    if (!text) {
        return null;
    }

    return <LoadingText text={text} iconTheme={iconTheme} renderText={renderText} />;
}

/**
 * PageLoading Component
 *
 * A full-page loading indicator component for displaying loading states during page transitions or authentication flows.
 */
export const PageLoading = forwardRef<HTMLDivElement, PageLoadingProps>(
    ({ text, iconSize = 20, iconTheme, indicator, renderText, className }, ref) => {
        return (
            <div
                ref={ref}
                className={cn(
                    'w-full h-full flex flex-col items-center justify-center gap-4',
                    className
                )}
                data-testid="page-loading"
            >
                {indicator ?? <LoadingIcon size={iconSize} theme={iconTheme} />}
                {text ? (
                    <LoadingText text={text} iconTheme={iconTheme} renderText={renderText} />
                ) : text === undefined ? (
                    <LocalizedLoadingText iconTheme={iconTheme} renderText={renderText} />
                ) : null}
            </div>
        );
    }
);

PageLoading.displayName = 'PageLoading';
